/**
 * 添付ファイル(申請時のスクショ・見積 / 購入後の領収書)の Storage 入出力。
 * パス設計: expenses/{txId}/attachments|receipts|received_receipts|settlement_receipts/...(storage.rules と対応)
 */
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Timestamp } from 'firebase/firestore';
import { getFirebaseStorage } from '@core/lib/firebase';
import type { StoredFile } from '@core/lib/types';

export const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ALLOWED_PREFIX = 'image/';
const ALLOWED_TYPES = ['application/pdf'];

export function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) return `${file.name}: 20MBを超えています`;
  if (!file.type.startsWith(ALLOWED_PREFIX) && !ALLOWED_TYPES.includes(file.type)) {
    return `${file.name}: 画像またはPDFのみ添付できます`;
  }
  return null;
}

/** ファイル名の衝突と、日本語ファイル名によるパス不整合を避ける */
function safeName(name: string): string {
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}${ext}`;
}

export async function uploadFiles(
  txId: string,
  kind: 'attachments' | 'receipts' | 'received_receipts' | 'settlement_receipts',
  files: File[],
  uploadedBy: string,
): Promise<StoredFile[]> {
  const storage = getFirebaseStorage();
  const results: StoredFile[] = [];
  for (const file of files) {
    const error = validateFile(file);
    if (error) throw new Error(error);
    const path = `expenses/${txId}/${kind}/${safeName(file.name)}`;
    await uploadBytes(ref(storage, path), file, { contentType: file.type });
    results.push({
      path,
      name: file.name,
      content_type: file.type,
      size: file.size,
      uploaded_by: uploadedBy,
      uploaded_at: Timestamp.now(),
    });
  }
  return results;
}

/** まとめて精算のファイル(2026-09-04)。settlements/{id}/list|evidence/... (storage.rules と対応) */
export async function uploadSettlementFiles(
  settlementId: string,
  kind: 'list' | 'evidence',
  files: File[],
  uploadedBy: string,
): Promise<StoredFile[]> {
  const storage = getFirebaseStorage();
  const results: StoredFile[] = [];
  for (const file of files) {
    const error = validateFile(file);
    if (error) throw new Error(error);
    const path = `settlements/${settlementId}/${kind}/${safeName(file.name)}`;
    await uploadBytes(ref(storage, path), file, { contentType: file.type });
    results.push({
      path,
      name: file.name,
      content_type: file.type,
      size: file.size,
      uploaded_by: uploadedBy,
      uploaded_at: Timestamp.now(),
    });
  }
  return results;
}

/** プレビュー用のURLを都度発行する(URLをDBに保存しない=権限変更が効くようにする) */
export async function getFileUrl(path: string): Promise<string> {
  return getDownloadURL(ref(getFirebaseStorage(), path));
}
